/**
 * R3-Phase-3 — operator's "Share video with viewers" modal.
 *
 * Opens from the Share button on /runs/:run_id.  Lets the operator
 * email-invite up to 50 recipients with optional company name +
 * custom message + expiry slider + confidentiality ack toggle.  Posts
 * to `/api/demo-runs/{run_id}/invitations` and shows per-recipient
 * status; on all-success, auto-closes after 2 s.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, ExternalLink, Loader2, ShieldCheck, ShieldAlert, X } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_RECIPIENTS = 50;
const MAX_MESSAGE = 280;
const EXPIRY_MARKS = [1, 7, 14, 30];

function parseEmails(raw) {
  if (!raw) return [];
  // Split on whitespace, comma, semicolon, newline.
  const tokens = raw
    .split(/[\s,;]+/)
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  const seen = new Set();
  const out = [];
  for (const t of tokens) {
    if (seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

function formatExpiryDate(days) {
  const d = new Date(Date.now() + Number(days) * 86_400_000);
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function StatusRow({ result }) {
  const ok = result.status === "sent";
  return (
    <div
      className="flex items-center gap-2 rounded border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs"
      data-testid={`invite-status-${result.email}`}
    >
      {ok ? (
        <Check size={13} className="shrink-0 text-emerald-600" />
      ) : (
        <X size={13} className="shrink-0 text-rose-600" />
      )}
      <span className="font-mono text-slate-800">{result.email}</span>
      <span className={ok ? "text-emerald-700" : "text-rose-700"}>
        {ok ? "Sent" : "Failed"}
      </span>
      {!ok && result.error ? (
        <span className="ml-1 truncate text-rose-600/70">— {result.error}</span>
      ) : null}
    </div>
  );
}

export default function ShareVideoModal({ runId, open, onOpenChange }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [emailsRaw, setEmailsRaw] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [expiresInDays, setExpiresInDays] = useState(14);
  const [requireAck, setRequireAck] = useState(true);
  const [overrideSuppression, setOverrideSuppression] = useState(false);
  // suppressionMap: { email: {suppressed, reason, source} } — populated by
  // a debounced check-recipients call as the operator edits the textarea.
  const [suppressionMap, setSuppressionMap] = useState({});
  const [results, setResults] = useState(null); // null | array

  const parsed = useMemo(() => parseEmails(emailsRaw), [emailsRaw]);
  const valid = parsed.filter((e) => EMAIL_RE.test(e));
  const invalid = parsed.filter((e) => !EMAIL_RE.test(e));
  const tooMany = valid.length > MAX_RECIPIENTS;
  const suppressedHits = valid.filter((e) => suppressionMap[e]?.suppressed);
  const hasSuppressed = suppressedHits.length > 0;

  // Debounced pre-flight suppression check.  We POST the full valid list
  // each time it changes (after a 350ms debounce) and store the resulting
  // map.  Server caps at 200 emails; we cap client-side at MAX_RECIPIENTS.
  useEffect(() => {
    if (!open) return;
    if (valid.length === 0) {
      setSuppressionMap({});
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const resp = await api.post("/invitations/check-recipients", {
          emails: valid.slice(0, MAX_RECIPIENTS),
        });
        setSuppressionMap(resp.data?.results || {});
      } catch {
        // Non-blocking: failure to check just leaves the chip set unmarked.
        setSuppressionMap({});
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [open, valid.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  function reset() {
    setEmailsRaw("");
    setCompanyName("");
    setCustomMessage("");
    setExpiresInDays(14);
    setRequireAck(true);
    setOverrideSuppression(false);
    setSuppressionMap({});
    setResults(null);
  }

  function handleOpenChange(next) {
    if (!next) reset();
    onOpenChange(next);
  }

  function removeEmail(e) {
    const remaining = valid.filter((x) => x !== e);
    setEmailsRaw(remaining.join(", "));
  }

  const submitMutation = useMutation({
    mutationFn: async ({ reissue = false } = {}) => {
      const body = {
        emails: valid,
        company_name: companyName.trim() || null,
        custom_message: customMessage.trim() || null,
        expires_in_days: Number(expiresInDays),
        require_confidentiality_ack: !!requireAck,
        override_suppression: isAdmin && hasSuppressed && overrideSuppression,
      };
      // R7 Item 8 — when the operator confirms a reissue, the same
      // body is re-POSTed with `?reissue=true` and the backend
      // revokes-and-supersedes any conflicting active invitations.
      const qs = reissue ? "?reissue=true" : "";
      return (await api.post(`/demo-runs/${runId}/invitations${qs}`, body)).data;
    },
    onSuccess: (data) => {
      const items = data.invitations || [];
      setResults(items);
      queryClient.invalidateQueries({ queryKey: ["run-invitations", runId] });
      const allOk = items.every((i) => i.status === "sent");
      if (allOk) {
        const reissuedCount = items.filter((i) => i.supersedes_invitation_id).length;
        const verb = data.reissued ? "Reissued" : "Sent";
        toast.success(
          `${verb} ${items.length} invitation${items.length === 1 ? "" : "s"}`
          + (reissuedCount ? ` (${reissuedCount} superseded prior)` : "")
        );
        setTimeout(() => {
          handleOpenChange(false);
        }, 2000);
      } else {
        const failed = items.filter((i) => i.status !== "sent").length;
        toast.error(`${failed} invitation${failed === 1 ? "" : "s"} failed — see status below`);
      }
    },
    onError: (err) => {
      // R7 Item 8 — surface the 409 with a confirm-then-reissue dialog
      // rather than the generic error toast.  We use the browser's
      // native confirm() to keep the modal lean; a richer AlertDialog
      // can be layered in later without changing the API contract.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.error === "active_invitation_exists") {
        const conflicts = detail.conflicts || [];
        const summary = conflicts.length
          ? conflicts.map((c) => c.email).join(", ")
          : "one or more recipients";
        const msg =
          `An active invitation already exists for: ${summary}.\n\n`
          + "Reissue will REVOKE the prior invitation(s) and mint fresh "
          + "magic-link URLs.  Anyone holding the old link will be locked "
          + "out.  Continue?";
        // eslint-disable-next-line no-alert
        if (window.confirm(msg)) {
          submitMutation.mutate({ reissue: true });
          return;
        }
        toast.message("Reissue cancelled — original invitation is still active.");
        return;
      }
      toast.error(formatApiError(err));
    },
  });

  const canSubmit =
    valid.length > 0 && !tooMany && !submitMutation.isPending && results === null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-xl"
        data-testid="share-video-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-indigo-600" />
            Share this demo video externally
          </DialogTitle>
          <DialogDescription>
            Invited viewers receive a unique link by email. Public anonymous sharing is disabled.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="invite-emails" className="text-xs font-semibold text-slate-700">
              Email recipients
            </Label>
            <Textarea
              id="invite-emails"
              data-testid="invite-emails-textarea"
              placeholder="Paste one or more emails (one per line, comma, or semicolon separated)"
              value={emailsRaw}
              onChange={(e) => setEmailsRaw(e.target.value)}
              className="min-h-[90px] text-sm"
              disabled={results !== null}
            />
            {valid.length > 0 || invalid.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 pt-1" data-testid="invite-chip-row">
                {valid.map((e) => {
                  const sup = suppressionMap[e];
                  // R3-Phase-5: rose chip + reason is a WARNING signal, not
                  // a privileged action — render it for every internal role
                  // (admin, builder, presenter).  Only the override TOGGLE
                  // (further down) is admin-gated.
                  if (sup?.suppressed) {
                    return (
                      <span
                        key={`s-${e}`}
                        className="inline-flex max-w-full items-center gap-1 rounded-full border border-rose-300 bg-rose-50 px-2 py-0.5 text-xs text-rose-800"
                        title={sup.source ? `Suppression source: ${sup.source}` : "Suppressed"}
                        data-testid={`invite-chip-suppressed-${e}`}
                      >
                        <ShieldAlert size={11} className="shrink-0" />
                        <span className="truncate">{e}</span>
                        <span className="ml-0.5 text-[10px] text-rose-600/80 truncate">
                          — {sup.reason || "suppressed"}
                        </span>
                        {results === null ? (
                          <button
                            type="button"
                            onClick={() => removeEmail(e)}
                            aria-label={`Remove ${e}`}
                            className="rounded-full p-0.5 text-rose-500 hover:bg-rose-100"
                          >
                            <X size={10} />
                          </button>
                        ) : null}
                      </span>
                    );
                  }
                  return (
                    <span
                      key={`v-${e}`}
                      className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-xs text-indigo-800"
                      data-testid={`invite-chip-${e}`}
                    >
                      {e}
                      {results === null ? (
                        <button
                          type="button"
                          onClick={() => removeEmail(e)}
                          aria-label={`Remove ${e}`}
                          className="rounded-full p-0.5 text-indigo-500 hover:bg-indigo-100"
                        >
                          <X size={10} />
                        </button>
                      ) : null}
                    </span>
                  );
                })}
                {invalid.map((e) => (
                  <span
                    key={`i-${e}`}
                    className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs text-rose-800"
                    data-testid={`invite-chip-invalid-${e}`}
                  >
                    {e} — not a valid email
                  </span>
                ))}
              </div>
            ) : null}
            {tooMany ? (
              <p className="text-xs text-rose-700">
                Max {MAX_RECIPIENTS} recipients per send. Remove {valid.length - MAX_RECIPIENTS} to continue.
              </p>
            ) : (
              <p className="text-[11px] text-slate-500">
                {valid.length} valid recipient{valid.length === 1 ? "" : "s"} · max {MAX_RECIPIENTS}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="invite-company" className="text-xs font-semibold text-slate-700">
                Company name (optional)
              </Label>
              <Input
                id="invite-company"
                data-testid="invite-company-input"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                maxLength={100}
                placeholder="Acme Inc."
                disabled={results !== null}
                className="text-sm"
              />
              <p className="text-[11px] text-slate-500">
                Used in the on-screen watermark.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="invite-message" className="text-xs font-semibold text-slate-700">
                Custom message (optional)
              </Label>
              <Textarea
                id="invite-message"
                data-testid="invite-message-textarea"
                value={customMessage}
                onChange={(e) =>
                  setCustomMessage(e.target.value.slice(0, MAX_MESSAGE))
                }
                placeholder="Add a short note for your recipients."
                className="h-[90px] text-sm"
                disabled={results !== null}
              />
              <p className="text-[11px] text-slate-500" data-testid="invite-message-count">
                {customMessage.length} / {MAX_MESSAGE} characters
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Expiry</Label>
            <Slider
              data-testid="invite-expiry-slider"
              min={1}
              max={30}
              step={1}
              value={[expiresInDays]}
              onValueChange={(v) => setExpiresInDays(v?.[0] ?? 14)}
              disabled={results !== null}
              className="pt-1"
            />
            <div className="flex items-center justify-between text-[11px] text-slate-500">
              <span>1d</span>
              <span>7d</span>
              <span>14d</span>
              <span>30d</span>
            </div>
            <p className="text-xs text-slate-600" data-testid="invite-expiry-label">
              Link expires in <strong>{expiresInDays} day{expiresInDays === 1 ? "" : "s"}</strong>{" "}
              (on {formatExpiryDate(expiresInDays)})
            </p>
          </div>

          <label
            className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
            data-testid="invite-ack-toggle"
          >
            <Checkbox
              checked={requireAck}
              onCheckedChange={(v) => setRequireAck(v === true)}
              disabled={results !== null}
              className="mt-0.5"
            />
            <div className="text-xs">
              <div className="font-semibold text-slate-800">
                Confidentiality acknowledgment required
              </div>
              <div className="text-slate-500">
                Recipient must acknowledge before viewing.
              </div>
            </div>
          </label>

          {/* R3-Phase-5: admin-only suppression override.  Hidden unless
              the current user is admin AND at least one recipient is
              currently on the email_suppressions list. */}
          {isAdmin && hasSuppressed && results === null ? (
            <label
              className="flex items-start gap-2 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2"
              data-testid="invite-override-suppression-toggle"
            >
              <Checkbox
                checked={overrideSuppression}
                onCheckedChange={(v) => setOverrideSuppression(v === true)}
                className="mt-0.5 border-rose-400 data-[state=checked]:bg-rose-600 data-[state=checked]:text-white"
              />
              <div className="text-xs">
                <div className="font-semibold text-rose-900">
                  Override and send anyway ({suppressedHits.length} suppressed)
                </div>
                <div className="text-rose-700/80">
                  Bypass the suppression list for this submission only. Audited.
                </div>
              </div>
            </label>
          ) : null}

          {results ? (
            <div
              className="space-y-1.5 rounded-lg border border-slate-200 bg-white p-2"
              data-testid="invite-results"
            >
              {results.map((r) => (
                <StatusRow key={r.invitation_id} result={r} />
              ))}
            </div>
          ) : null}

          <p className="text-[11px] text-slate-500">
            <a
              href="/help/sharing-demos-externally"
              target="_blank"
              rel="noopener"
              className="inline-flex items-center gap-1 text-indigo-700 hover:underline"
              data-testid="invite-help-link"
            >
              Learn how external sharing works
              <ExternalLink size={11} />
            </a>
          </p>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            data-testid="share-video-modal-close"
          >
            Close
          </Button>
          {results === null ? (
            <Button
              type="button"
              disabled={!canSubmit}
              onClick={() => submitMutation.mutate()}
              data-testid="share-video-modal-submit"
              className="bg-indigo-700 hover:bg-indigo-600"
            >
              {submitMutation.isPending ? (
                <>
                  <Loader2 size={13} className="mr-1.5 animate-spin" />
                  Sending…
                </>
              ) : (
                `Send invitations`
              )}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
